import React, { useState, useEffect, useCallback } from 'react';
import { getAnalysis } from '../../services/AIService';
import { useIntl } from 'react-intl';

// Module-level cache: the analysis is fetched ONCE on first visit and then kept
// across navigation. It only refreshes when the user clicks the refresh button.
const aiCache = { data: null, lastUpdated: null };

function AIInsights() {

    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });

    const [analysis, setAnalysis] = useState(aiCache.data);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [lastUpdated, setLastUpdated] = useState(aiCache.lastUpdated);

    const loadAnalysis = useCallback(() => {
        setLoading(true);
        setError('');
        getAnalysis('MARKET_SUMMARY', 'BNBUSDC')
            .then(result => {
                aiCache.data = result;
                aiCache.lastUpdated = new Date();
                setAnalysis(result);
                setLastUpdated(aiCache.lastUpdated);
            })
            .catch(err => {
                const msg = err.response ? err.response.data : err.message;
                setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        // Fetch only the first time (no cached data yet); afterwards keep what we have.
        if (!aiCache.data) loadAnalysis();
    }, [loadAnalysis]);

    function getRecommendationColor(rec) {
        switch (rec) {
            case 'STRONG_BUY': return 'success';
            case 'BUY': return 'info';
            case 'HOLD': return 'warning';
            case 'SELL': return 'danger';
            case 'STRONG_SELL': return 'danger';
            default: return 'secondary';
        }
    }

    function getConfidenceWidth(confidence) {
        return Math.min(Math.max(confidence || 0, 0), 100);
    }

    function getConfidenceColor(confidence) {
        if (confidence >= 75) return 'success';
        if (confidence >= 50) return 'warning';
        return 'danger';
    }

    function parseResponse(response) {
        if (!response) return null;
        try {
            return typeof response === 'string' ? JSON.parse(response) : response;
        } catch {
            return null;
        }
    }

    const parsed = analysis ? parseResponse(analysis.response) : null;

    return (
        <div className="col-12 col-lg-6 mb-4">
            <div className="card border-0 shadow">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                        </svg>
                        {t('dashboard.ai.title')}
                    </h5>
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={loadAnalysis}
                        disabled={loading}
                    >
                        {loading ? (
                            <span className="spinner-border spinner-border-sm" role="status"></span>
                        ) : (
                            <svg className="icon icon-xs" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                            </svg>
                        )}
                    </button>
                </div>
                <div className="card-body">
                    {error && (
                        <div className="alert alert-warning py-2">
                            <small>{error}</small>
                        </div>
                    )}
                    {!error && !analysis && !loading && (
                        <p className="text-muted mb-0">{t('dashboard.ai.unavailable')}</p>
                    )}
                    {loading && !analysis && (
                        <div className="text-center py-3">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="text-muted mt-2 mb-0">{t('dashboard.ai.loading')}</p>
                        </div>
                    )}
                    {parsed && (
                        <React.Fragment>
                            {analysis.recommendation && (
                                <div className="mb-3 text-center">
                                    <span className={`badge bg-${getRecommendationColor(analysis.recommendation)} fs-6 px-3 py-2`}>
                                        {analysis.recommendation.replace('_', ' ')}
                                    </span>
                                </div>
                            )}
                            {analysis.confidence && (
                                <div className="mb-3">
                                    <div className="d-flex justify-content-between mb-1">
                                        <small className="text-muted">{t('dashboard.ai.confidence')}</small>
                                        <small className="fw-bold">{parseFloat(analysis.confidence)}%</small>
                                    </div>
                                    <div className="progress" style={{ height: '6px' }}>
                                        <div
                                            className={`progress-bar bg-${getConfidenceColor(parseFloat(analysis.confidence))}`}
                                            role="progressbar"
                                            style={{ width: `${getConfidenceWidth(parseFloat(analysis.confidence))}%` }}
                                        ></div>
                                    </div>
                                </div>
                            )}
                            {parsed.summary && (
                                <div className="mb-3">
                                    <small className="text-muted d-block mb-1">{t('dashboard.ai.summary')}</small>
                                    <p className="mb-0" style={{ fontSize: '0.9em', overflowWrap: 'anywhere' }}>{parsed.summary}</p>
                                </div>
                            )}
                            {parsed.riskFactors && parsed.riskFactors.length > 0 && (
                                <div className="mb-2">
                                    <small className="text-muted d-block mb-1">{t('dashboard.ai.riskFactors')}</small>
                                    <ul className="ps-3 mb-0" style={{ fontSize: '0.85em', overflowWrap: 'anywhere' }}>
                                        {parsed.riskFactors.map((factor, i) => (
                                            <li key={i} className="text-gray-600 mb-1">{factor}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {lastUpdated && (
                                <div className="text-end">
                                    <small className="text-muted">
                                        {t('dashboard.ai.updated')} {lastUpdated.toLocaleTimeString()}
                                    </small>
                                </div>
                            )}
                        </React.Fragment>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AIInsights;
